@use "../../../styles/breakpoint.module.scss" as *;

:root {
    --header-height: 80px;

    @media ($below-mobile) {
        --header-height: 64px;
    }
}

.root {
    display: flex;
    flex-direction: row;
    height: var(--header-height);
    gap: var(--spacer-24);
    padding-inline: var(--spacer-24);
    min-height: calc(1lh + var(--spacer-24) * 2);
    align-items: center;
    box-shadow: var(--box-shadow-1);

    [role="separator"] {
        height: 100%;
    }

    // Three columns on desktop: nav group, logo, CTA group. The outer
    // columns always match in width, so the logo stays centred on the page
    // however wide either group is.
    @media ($above-tablet) {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    }

    // Tighter spacing between header items so the nav titles keep their
    // room at the smallest desktop width.
    @media ($below-laptop) {
        gap: var(--spacer-16);
    }

    @media ($below-tablet) {
        gap: 0;

        .navGroup > *:not(.iconButton),
        .ctaGroup > *:not(.iconButton),
        .hideOnMobile {
            display: none;
        }
    }
}

.navGroup,
.ctaGroup {
    display: flex;
    align-items: center;
    align-self: stretch;
    gap: inherit;
    min-width: 0;

    // On mobile the groups' children sit directly in .root's flex row.
    @media ($below-tablet) {
        display: contents;
    }
}

// Only the nav columns give up width when space is tight.
.navGroup > *:not(.navItems, .spacer),
.ctaGroup > * {
    flex-shrink: 0;
}

.ctaGroup {
    justify-content: flex-end;
}

.menuTrigger {
    composes: base from "../../Clickables/index.module.scss";
    composes: fontST16 from "../../../styles/typography.module.scss";
    break-inside: avoid;
    white-space: nowrap;
    // Links would inherit the body's letter-spacing; buttons (dropdown
    // titles) don't. Pin it so both look the same.
    letter-spacing: normal;

    // Only the title that was hovered, clicked or focused, not every
    // expanded title.
    &[data-active] {
        color: var(--color-contrast);
    }
}

.iconButton {
    composes: outline from "../../Clickables/index.module.scss";
    composes: buttonReset from "../../Clickables/index.module.scss";
    color: var(--color-text);

    svg {
        width: 24px;
        height: 24px;
    }

    &[data-hovered] {
        color: var(--color-contrast);
    }
}

.logo {
    flex-grow: 1;
    display: flex;
    align-items: stretch;
    justify-content: center;
    max-height: 32px;
    height: 100%;

    svg {
        width: 100%;
        height: auto;
        color: var(--color-text);
        margin: 0;
    }

    // On desktop the logo sizes to its own width in .root's centre column.
    @media ($above-tablet) {
        height: 32px;

        svg {
            width: auto;
            height: 100%;
        }
    }
}

.spacer {
    flex-grow: 1;
}

// Stacking context for the expanded nav: overlay < panel background <
// link columns, all above page content (and ScrollNav).
.header {
    position: relative;
    z-index: 52;
}

// Titles are separated by one shared gap (--nav-gap), so the space between
// the end of one title and the start of the next is always the same. The
// Header component measures the link columns and sets the gap just wide
// enough that no column runs into the next title; the default here is
// close to the measured value to minimise shift before hydration.
.navItems {
    align-self: stretch;
    flex: 0 1 auto;
    min-width: 0;
    display: flex;
    gap: var(--nav-gap, 80px);
}

// The title sets the column's width; its links hang beneath it in the
// expanded panel without widening the column.
.navColumn {
    position: relative;
    align-self: stretch;
    display: flex;
    align-items: center;
}

.navColumnLinks {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: var(--spacer-12);
    width: max-content;
    // Wrap rather than run into the next title when the gap had to shrink.
    max-width: calc(100% + var(--nav-gap, 80px) - var(--spacer-16));
    margin: 0;
    padding: var(--spacer-24) 0 0;
    list-style: none;

    .navColumn:last-child > & {
        max-width: none;
    }

    &[data-hidden] {
        visibility: hidden;
    }
}

.navColumnLink {
    composes: base from "../../Clickables/index.module.scss";
    composes: fontST16 from "../../../styles/typography.module.scss";
    display: block;
    line-height: 24px;
    // Links are muted except in the active column (the title last hovered,
    // clicked or focused, or the column the pointer is in).
    color: var(--dark-gray);

    .navColumn[data-active] & {
        color: var(--color-text);
    }

    .navColumn &[data-hovered] {
        color: var(--color-contrast);
    }
}

// --nav-panel-height is the tallest link column, measured in the Header
// component (columns include their own top padding).
.navExpansion {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1;
    height: calc(var(--nav-panel-height, 0px) + var(--spacer-32));
    background: var(--color-primary);
    border-top: 1px solid var(--mid-gray);
    box-shadow: var(--box-shadow-1);
}

.dropdownOverlay {
    position: fixed;
    inset: 0;
    top: var(--header-height);
    z-index: 0;
    background: var(--transparent-40);

    body:has(&) {
        overflow: hidden;
    }
}

.showOnMobile {
    @media ($above-tablet) {
        display: none;
    }
}

.openMobileMenu[aria-expanded="true"]::before {
    content: "";
    position: absolute;
    top: 0;
    right: var(--header-height);
    height: var(--header-height);
    width: 1px;
    background: var(--mid-gray);
}

.mobileMenuOverlay {
    composes: grid from "../../../styles/layout.module.scss";
    position: fixed;
    top: var(--header-height);
    left: 0;
    width: 100vw;
    height: calc(100vh - var(--header-height));
    background:
        linear-gradient(to right, var(--mid-gray), var(--mid-gray)) no-repeat
            top left / calc(100% - var(--header-height) + 1px) 1px,
        var(--color-primary);
    z-index: 100;
    overflow-y: auto;
    align-content: start;

    body:has(&) {
        overflow: hidden;
    }

    @media ($above-tablet) {
        display: none;
    }
}

.mobileMenuTopBar {
    grid-column: content;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    padding-block: var(--spacer-40);
}

.mobileBackButton {
    justify-self: start;
    background: transparent;
    border: 0;
    padding: 0;
    line-height: 0;
    cursor: pointer;
    color: inherit;

    svg {
        width: 16px;
        height: 16px;
        transform: scaleX(-1);
    }
}

.mobileMenuTitle {
    composes: fontHB48 from "../../../styles/typography.module.scss";
    color: var(--black);
    text-align: center;
}

.mobileMenuItem {
    grid-column: 1 / -1;
    composes: fontHB48 from "../../../styles/typography.module.scss";
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacer-16);
    padding: var(--spacer-40) var(--container-min-margin);
    background: transparent;
    border: 0;
    border-bottom: 1px solid var(--mid-gray);
    color: var(--black);
    text-align: left;
    text-decoration: none;
    cursor: pointer;

    svg {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
        margin: 0;
    }
}

.mobileMenuButtons {
    grid-column: content;
    display: flex;
    gap: var(--spacer-16);
    padding-block: var(--spacer-24);
}

.mobileMenuButton {
    flex: 1;
    justify-content: center;
}
